<script setup>
import { computed, defineEmits, defineProps, ref, toRefs, watch } from "vue";
import HousingCards from "@/components/ExperiencePage/ProgramDetailsSection/housing/components/HousingCards";
import ApplyButton from "@/components/shared/Button/ApplyButton.vue";
import APIValidationBadge from "@/components/shared/icons/APIValidationBadge.vue";
import { ACTIVE_STATUS, API_ENTITY_INFORMATION } from "@/constants.js";
import ordersService from "@/services/orders.js";
import { pastSessionDeadline } from "@/util/pastSessionDeadline";
import { priceLabel } from "@/util/pricingLabel.js";
import { TENANT_HOUSING_TEXT_MAP } from "./constants.js";

defineOptions({
  name: "HousingSectionBlock",
});

const emit = defineEmits(["selectSession", "personalizeAndApply", "observeSection"]);

const props = defineProps({
  sessionId: {
    type: String,
    required: true,
  },
  sessions: {
    type: Array,
    required: true,
  },
  externalLink: {
    type: String,
    default: "",
  },
  registerType: {
    type: String,
    default: "Personalize & Apply",
  },
});

const { sessionId, sessions } = toRefs(props);

const getSelectedSession = () => sessions.value?.find((session) => session?.id === sessionId.value) || {};

const selectedSession = ref(getSelectedSession());
const roomPrices = ref([]);
const completedFetchingRooms = ref(false);

watch(sessionId, () => {
  selectedSession.value = getSelectedSession();
  loadRoomPrices();
});

const sessionHousing = computed(() => {
  const housings = [];

  selectedSession.value?.session_units?.forEach((session_unit) => {
    const housingIndex = housings.findIndex((housing) => housing.id === session_unit?.unit?.housings?.id);
    if (session_unit?.unit?.housings?.id && housingIndex < 0) {
      housings.push({
        id: session_unit.unit.housings.id,
        description: session_unit.unit.housings.description,
        startingPrice: "",
        status: session_unit.unit.housings.status,
        owner_entity_id: session_unit.unit.housings?.owner_entity_id,
        subcategory: session_unit.unit.housings?.subcategory,
        supporting_media: session_unit.unit.housings?.supporting_media,
        tier: session_unit.unit.housings?.tier,
        type: session_unit.unit.housings?.type,
        rooms: session_unit.unit?.rooms ?? [],
      });
    } else if (housingIndex >= 0) {
      // Add missing rooms
      session_unit?.unit?.rooms?.forEach((room) => {
        const roomExists = housings[housingIndex].rooms.findIndex((housingRoom) => housingRoom.id === room.id);
        if (roomExists < 0) {
          housings[housingIndex].rooms.push(room);
        }
      });
    }
  });

  return housings.map((housing) => ({
    ...housing,
    startingPrice: completedFetchingRooms.value
      ? priceLabel(roomPrices.value, selectedSession.value?.default_room_id, housing.rooms, true)
      : "",
  }));
});

const activeHousings = computed(() => sessionHousing.value.filter((housing) => housing.status === ACTIVE_STATUS));

watch(
  () => sessionHousing.value,
  (newSessionHousing) => {
    if (newSessionHousing) {
      emit("observeSection", "housing");
    }
  },
);

const goToPersonalizeAndApply = () => {
  emit("personalizeAndApply", sessionId.value);
};
const loadRoomPrices = () => {
  roomPrices.value = [];
  const sessionUnits = selectedSession.value?.session_units ?? [];

  // exit early if there are no rooms to price
  if (sessionUnits.length < 1) {
    return;
  }
  //flatmap: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flatMap
  const roomIds = sessionUnits.flatMap((sessionUnit) =>
    (sessionUnit?.unit?.rooms ?? [])
      .filter((room) => room?.status === ACTIVE_STATUS)
      .map((room) => room?.id)
      .filter(Boolean),
  );

  completedFetchingRooms.value = false;
  ordersService
    .getOrdersPricing({
      payload: {
        room_ids: roomIds,
        session_id: selectedSession.value?.id,
      },
    })
    .then((response) => {
      const roomPriceObjects = response.data?.data?.rooms ?? [];
      roomPrices.value = Object.fromEntries(
        roomPriceObjects.map((obj) => [obj.product_id, obj.student_total_price_in_cents]),
      );
    })
    .finally(() => {
      completedFetchingRooms.value = true;
    });
};

const displayVerifiedStamp = computed(
  () =>
    activeHousings.value.length > 0 &&
    activeHousings.value.every((housing) => housing?.owner_entity_id === API_ENTITY_INFORMATION.id),
);

const housingSectionText = computed(() => {
  if (displayVerifiedStamp.value) {
    return TENANT_HOUSING_TEXT_MAP[API_ENTITY_INFORMATION.id] || TENANT_HOUSING_TEXT_MAP.generic;
  }

  return TENANT_HOUSING_TEXT_MAP.generic;
});
</script>

<template>
  <div v-if="sessionHousing?.length > 0" class="pt-20 px-4 md:px-8 text-indigo-base">
    <div class="flex flex-col lg:flex-row gap-4">
      <div class="h-fit w-full flex flex-col grow gap-4">
        <div class="justify-start">
          <div class="self-center w-full">
            <div class="flex self-center">
              <h2 class="self-center uppercase font-bold text-4xl">Housing</h2>
              <div v-if="displayVerifiedStamp" class="w-full">
                <APIValidationBadge custom-class="pl-2 w-20 h-20 float-right md:float-none" />
              </div>
            </div>
          </div>
        </div>
        <div class="block h-fit w-full">
          {{ housingSectionText }}
        </div>
      </div>
    </div>
    <HousingCards :housings="activeHousings" />
    <div class="mt-20 md:h-30 flex justify-end">
      <div class="w-full lg:w-96 grid md:grid-rows-2 grid-flow-rows gap-2 md:gap-4">
        <div>
          <ApplyButton
            :id="sessionId"
            :disabled="pastSessionDeadline(selectedSession)"
            type="button"
            show-arrow
            :outlined="true"
            :external-link="props.externalLink"
            :register-type="props.registerType"
            class="py-2 mt-3 xs:mt-0 text-sm"
            event-category="Program Page Personalize CTA"
            @click="goToPersonalizeAndApply"
          />
        </div>
      </div>
    </div>
    <hr class="my-1 md:mt-20 border-b-3 border-gray-200" />
  </div>
</template>

<style scoped>
.bookmark-icon > svg {
  fill: #1e264c;
}
.bookmark-icon:hover > svg {
  fill: white;
}
.session-select-component-style :deep(.vs__dropdown-toggle) {
  overflow: auto;
  max-height: fit-content;
  display: flex;
  border-color: white;
  padding-left: 0.1rem;
  padding-right: 0.1rem;
}
.session-select-component-style :deep(.vs__selected-options) {
  display: contents;
  overflow: hidden;
}
.session-select-component-style :deep(.vs__dropdown-menu) {
  border-radius: 0;
  display: block;
  overflow: hidden;
  word-wrap: break-word;
}
.session-select-component-style :deep(.vs__dropdown-option) {
  white-space: normal;
}
.session-select-component-style :deep(.vs__dropdown-option--highlight) {
  background: #edf9f8 !important;
  cursor: auto;
}
.session-select-component-style :deep(.vs__selected) {
  display: block;
  width: 100%;
  position: relative !important;
}
</style>
